// Holidays: which traditions keep them, when they fall, and how the app marks them.
// Gregorian dates and Easter are computed here. Lunar dates (Hebrew, Islamic, Chinese, Hindu,
// Buddhist) come from content/holiday-dates.json, built by holidayCalendars.ts and checked by
// holidays.test.ts, so the app never depends on the device's calendar support.
import table from '../../content/holiday-dates.json';
import { addDays, daysBetween } from './dates';
import type { Day } from '../types';

/** What the user picks in settings: the traditions whose holidays they keep. */
export const TRADITIONS = [
  'seasonal', 'christianRomanCatholic', 'christianEvangelical', 'christianLutheran', 'christianGreekOrthodox', 'christianRussianOrthodox',
  'christianCoptic', 'christianMaronite', 'christianMelkite', 'christianGreekCatholic', 'mormon', 'jehovah', 'jewish',
  'islamicSunni', 'islamicSunniHanafi', 'islamicSunniSalafi', 'islamicSunniMaliki', 'islamicSunniShafi', 'islamicShia', 'islamicShiaIsmailli',
  'druze', 'hindu', 'sikh', 'buddhist', 'shinto', 'chineseLunar', 'taoist', 'confucian',
] as const;
export type Tradition = (typeof TRADITIONS)[number];

// Jehovah's Witnesses keep no holidays (only the Memorial), so they appear in no list below.
const WESTERN: Tradition[] = ['christianRomanCatholic', 'christianEvangelical', 'christianLutheran', 'christianMaronite', 'christianMelkite', 'christianGreekCatholic', 'mormon'];
const ORTHODOX: Tradition[] = ['christianGreekOrthodox', 'christianRussianOrthodox', 'christianCoptic'];
const ISLAMIC: Tradition[] = ['islamicSunni', 'islamicSunniHanafi', 'islamicSunniSalafi', 'islamicSunniMaliki', 'islamicSunniShafi', 'islamicShia', 'islamicShiaIsmailli'];
const CHINESE: Tradition[] = ['chineseLunar', 'taoist', 'confucian'];

type Rule =
  | { fixed: [month: number, day: number] }
  | { easter: number; julian?: true } // days after Easter Sunday (Julian computus for the Orthodox)
  | { table: true };

export type HolidayDef = {
  traditions: Tradition[];
  rule: Rule;
  /** Length in days, from the main day (table holidays carry their own end). */
  days: number;
  /** Starts the evening before (Jewish holidays, New Year's Eve dinners). */
  eve?: true;
  /** Decorations: the first is the holiday's symbol. */
  decor: string[];
  /** Play the falling-decoration animation (off for solemn days). */
  animate: boolean;
};

export const HOLIDAYS = {
  newYear: { traditions: ['seasonal'], rule: { fixed: [12, 31] }, days: 2, decor: ['🎆', '🥂', '🎉', '✨'], animate: true },
  valentines: { traditions: ['seasonal'], rule: { fixed: [2, 14] }, days: 1, decor: ['❤️', '🌹', '💌'], animate: true },
  halloween: { traditions: ['seasonal'], rule: { fixed: [10, 31] }, days: 1, decor: ['🎃', '🦇', '👻', '🍬'], animate: true },

  carnival: { traditions: ['christianRomanCatholic', 'christianLutheran'], rule: { easter: -52 }, days: 6, decor: ['🎭', '🎊', '🍩'], animate: true },
  easter: { traditions: WESTERN, rule: { easter: -2 }, days: 4, decor: ['🥚', '🐣', '🌷', '🐰'], animate: true },
  christmas: { traditions: [...WESTERN, 'christianGreekOrthodox'], rule: { fixed: [12, 24] }, days: 3, decor: ['🎄', '⭐', '❄️', '🎁'], animate: true },
  orthodoxEaster: { traditions: ORTHODOX, rule: { easter: -2, julian: true }, days: 4, decor: ['🥚', '🕯️', '🌷'], animate: true },
  orthodoxChristmas: { traditions: ['christianRussianOrthodox', 'christianCoptic'], rule: { fixed: [1, 6] }, days: 2, decor: ['🎄', '⭐', '❄️'], animate: true },

  roshHashanah: { traditions: ['jewish'], rule: { table: true }, days: 2, eve: true, decor: ['🍎', '🍯', '🍞'], animate: true },
  yomKippur: { traditions: ['jewish'], rule: { table: true }, days: 1, eve: true, decor: ['🕯️'], animate: false },
  sukkot: { traditions: ['jewish'], rule: { table: true }, days: 7, eve: true, decor: ['🌿', '🍋', '🌴'], animate: true },
  hanukkah: { traditions: ['jewish'], rule: { table: true }, days: 8, eve: true, decor: ['🕎', '✨', '🍩'], animate: true },
  purim: { traditions: ['jewish'], rule: { table: true }, days: 1, eve: true, decor: ['🎭', '🎉'], animate: true },
  passover: { traditions: ['jewish'], rule: { table: true }, days: 8, eve: true, decor: ['🫓', '🍷', '🌿'], animate: true },
  shavuot: { traditions: ['jewish'], rule: { table: true }, days: 2, eve: true, decor: ['🌾', '🧀', '🌸'], animate: true },

  ramadan: { traditions: ISLAMIC, rule: { table: true }, days: 30, decor: ['🌙', '⭐', '🏮'], animate: true },
  eidAlFitr: { traditions: ISLAMIC, rule: { table: true }, days: 3, decor: ['🌙', '✨', '🎁'], animate: true },
  eidAlAdha: { traditions: [...ISLAMIC, 'druze'], rule: { table: true }, days: 4, decor: ['🐑', '🌙', '✨'], animate: true },

  lunarNewYear: { traditions: CHINESE, rule: { table: true }, days: 7, eve: true, decor: ['🏮', '🧧', '🧨', '🐉'], animate: true },
  lanternFestival: { traditions: CHINESE, rule: { table: true }, days: 1, decor: ['🏮', '🌕'], animate: true },
  dragonBoat: { traditions: CHINESE, rule: { table: true }, days: 1, decor: ['🐉', '🛶'], animate: true },
  midAutumn: { traditions: CHINESE, rule: { table: true }, days: 1, decor: ['🥮', '🌕', '🏮'], animate: true },

  shogatsu: { traditions: ['shinto'], rule: { fixed: [12, 31] }, days: 4, decor: ['🎍', '⛩️', '🌅'], animate: true },
  setsubun: { traditions: ['shinto'], rule: { table: true }, days: 1, decor: ['👹', '🫘'], animate: true },
  hinamatsuri: { traditions: ['shinto'], rule: { fixed: [3, 3] }, days: 1, decor: ['🎎', '🌸'], animate: true },
  tanabata: { traditions: ['shinto'], rule: { fixed: [7, 7] }, days: 1, decor: ['🎋', '⭐'], animate: true },
  obon: { traditions: ['buddhist', 'shinto'], rule: { fixed: [8, 13] }, days: 4, decor: ['🏮', '🕯️'], animate: true },
  vesak: { traditions: ['buddhist'], rule: { table: true }, days: 1, decor: ['🪷', '🏮'], animate: true },

  holi: { traditions: ['hindu'], rule: { table: true }, days: 2, decor: ['🎨', '🌈', '💧'], animate: true },
  diwali: { traditions: ['hindu', 'sikh'], rule: { table: true }, days: 5, decor: ['🪔', '✨', '🎆'], animate: true },
  vaisakhi: { traditions: ['sikh', 'hindu'], rule: { fixed: [4, 14] }, days: 1, decor: ['🌾', '🥁'], animate: true },
  guruNanakJayanti: { traditions: ['sikh'], rule: { table: true }, days: 1, decor: ['🪔', '🙏'], animate: true },
} satisfies Record<string, HolidayDef>;

export type HolidayId = keyof typeof HOLIDAYS;
export const HOLIDAY_IDS = Object.keys(HOLIDAYS) as HolidayId[];
export const TABLE_HOLIDAYS = HOLIDAY_IDS.filter(id => 'table' in HOLIDAYS[id].rule);

type Table = { from: number; to: number; dates: Record<string, Partial<Record<HolidayId, [Day, Day][]>>> };
const TABLE = table as unknown as Table;
export const TABLE_YEARS = [TABLE.from, TABLE.to] as const;

const pad = (n: number) => String(n).padStart(2, '0');

/** Easter Sunday: Gregorian (anonymous algorithm) or Julian computus shown as a Gregorian date. */
export function easterSunday(year: number, julian = false): Day {
  if (julian) {
    const a = year % 4, b = year % 7, c = year % 19;
    const d = (19 * c + 15) % 30;
    const e = (2 * a + 4 * b - d + 34) % 7;
    const month = Math.floor((d + e + 114) / 31);
    const day = ((d + e + 114) % 31) + 1;
    // Julian → Gregorian: 13 days between 1900 and 2099.
    return addDays(`${year}-${pad(month)}-${pad(day)}`, 13);
  }
  const a = year % 19, b = Math.floor(year / 100), c = year % 100, d = Math.floor(b / 4), e = b % 4;
  const f = Math.floor((b + 8) / 25), g = Math.floor((b - f + 1) / 3), h = (19 * a + b - d - g + 15) % 30;
  const i = Math.floor(c / 4), k = c % 4, l = (32 + 2 * e + 2 * i - h - k) % 7, m = Math.floor((a + 11 * h + 22 * l) / 451);
  const month = Math.floor((h + l - 7 * m + 114) / 31);
  const day = ((h + l - 7 * m + 114) % 31) + 1;
  return `${year}-${pad(month)}-${pad(day)}`;
}

export type Occurrence = { id: HolidayId; start: Day; end: Day; main: Day };

/** Every occurrence of a holiday that starts in `year` (lunar holidays can fall twice in one year). */
export function occurrences(id: HolidayId, year: number): Occurrence[] {
  const def: HolidayDef = HOLIDAYS[id];
  let spans: [Day, Day][];
  if ('fixed' in def.rule) {
    const main = `${year}-${pad(def.rule.fixed[0])}-${pad(def.rule.fixed[1])}`;
    spans = [[main, addDays(main, def.days - 1)]];
  } else if ('easter' in def.rule) {
    const main = addDays(easterSunday(year, def.rule.julian), def.rule.easter);
    spans = [[main, addDays(main, def.days - 1)]];
  } else {
    spans = TABLE.dates[year]?.[id] ?? [];
  }
  return spans.map(([main, end]) => ({ id, main, end, start: def.eve ? addDays(main, -1) : main }));
}

/** Holidays of the user's traditions on each day from `from` to `to` (for the planner). */
export function holidaysByDay(from: Day, to: Day, traditions: readonly Tradition[]): Map<Day, Occurrence[]> {
  const out = new Map<Day, Occurrence[]>();
  if (!traditions.length) return out;
  const keep = new Set(traditions);
  for (const id of HOLIDAY_IDS) {
    if (!HOLIDAYS[id].traditions.some((tr: Tradition) => keep.has(tr))) continue;
    for (let y = Number(from.slice(0, 4)) - 1; y <= Number(to.slice(0, 4)); y++) {
      for (const o of occurrences(id, y)) {
        for (let d = o.start < from ? from : o.start; d <= o.end && d <= to; d = addDays(d, 1)) out.set(d, [...(out.get(d) ?? []), o]);
      }
    }
  }
  return out;
}

export type HolidayNear = Occurrence & { /** 0 while it is on, else days until it starts. */ daysUntil: number };

/** Holidays of the user's traditions that are on today or start within `lead` days, soonest first. */
export function holidaysNear(today: Day, traditions: readonly Tradition[], lead = 7): HolidayNear[] {
  if (!traditions.length) return [];
  const keep = new Set(traditions);
  const year = Number(today.slice(0, 4));
  const out: HolidayNear[] = [];
  for (const id of HOLIDAY_IDS) {
    if (!HOLIDAYS[id].traditions.some((tr: Tradition) => keep.has(tr))) continue;
    for (const y of [year - 1, year, year + 1]) {
      for (const o of occurrences(id, y)) {
        if (o.end < today || daysBetween(today, o.start) > lead) continue;
        out.push({ ...o, daysUntil: Math.max(0, daysBetween(today, o.start)) });
      }
    }
  }
  return out.sort((a, b) => a.daysUntil - b.daysUntil || a.start.localeCompare(b.start));
}
